import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Banner from '../../components/Banner';
import ConfirmDialog from '../../components/ConfirmDialog';
import { useMessage } from '../../components/MessageProvider';
import { useApi } from '../../hooks/useApi';
import type { AdminCommissionsResponse, AdminDiscountCode, AdminDiscountCodeRequest } from '../../lib/apiClient';

type CodeDraft = {
  code: string;
  provider_name: string;
  provider_email: string;
  trial_days: string;
  commission_usd: string;
  ends_on: string;
  is_enabled: boolean;
};

const EMPTY_DRAFT: CodeDraft = {
  code: '',
  provider_name: '',
  provider_email: '',
  trial_days: '30',
  commission_usd: '0.00',
  ends_on: '',
  is_enabled: true,
};

function formatUsd(value: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
}

function toDraft(code: AdminDiscountCode): CodeDraft {
  return {
    code: code.code,
    provider_name: code.provider_name,
    provider_email: code.provider_email,
    trial_days: String(code.trial_days),
    commission_usd: code.commission_usd.toFixed(2),
    ends_on: code.ends_on || '',
    is_enabled: code.is_enabled,
  };
}

export default function AdminDiscounts() {
  const {
    getAdminDiscountCodes,
    createAdminDiscountCode,
    updateAdminDiscountCode,
    deleteAdminDiscountCode,
    getAdminCommissions,
    settleAdminCommissions,
  } = useApi();
  const { showSuccess } = useMessage();
  const [tab, setTab] = React.useState(0);
  const [codes, setCodes] = React.useState<AdminDiscountCode[]>([]);
  const [commissions, setCommissions] = React.useState<AdminCommissionsResponse>();
  const [loading, setLoading] = React.useState(true);
  const [showArchived, setShowArchived] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [editing, setEditing] = React.useState<AdminDiscountCode | null>(null);
  const [draft, setDraft] = React.useState<CodeDraft>(EMPTY_DRAFT);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [archiveTarget, setArchiveTarget] = React.useState<AdminDiscountCode | null>(null);
  const [settlingProviderId, setSettlingProviderId] = React.useState<number | null>(null);

  const loadData = React.useCallback(async () => {
    setLoading(true);
    const [codesResult, commissionsResult] = await Promise.all([
      getAdminDiscountCodes({ shouldShowError: () => true }),
      getAdminCommissions({ shouldShowError: () => true }),
    ]);
    if (codesResult.ok) setCodes(codesResult.data.codes);
    if (commissionsResult.ok) setCommissions(commissionsResult.data);
    setLoading(false);
  }, [getAdminCommissions, getAdminDiscountCodes]);

  React.useEffect(() => { void loadData(); }, [loadData]);

  const openCreate = () => {
    setEditing(null);
    setDraft(EMPTY_DRAFT);
    setDialogOpen(true);
  };

  const openEdit = (code: AdminDiscountCode) => {
    setEditing(code);
    setDraft(toDraft(code));
    setDialogOpen(true);
  };

  const saveCode = async () => {
    const payload: AdminDiscountCodeRequest = {
      code: draft.code.trim(),
      provider_name: draft.provider_name.trim(),
      provider_email: draft.provider_email.trim(),
      trial_days: Number.parseInt(draft.trial_days, 10),
      commission_usd: Number.parseFloat(draft.commission_usd),
      ends_on: draft.ends_on || null,
      is_enabled: draft.is_enabled,
    };
    setSaving(true);
    const result = editing
      ? await updateAdminDiscountCode(editing.discount_code_id, payload)
      : await createAdminDiscountCode(payload);
    setSaving(false);
    if (result.ok) {
      setDialogOpen(false);
      showSuccess(editing ? 'Discount code updated.' : 'Discount code created.');
      await loadData();
    }
  };

  const archiveCode = async () => {
    if (!archiveTarget) return;
    const result = await deleteAdminDiscountCode(archiveTarget.discount_code_id);
    if (result.ok) {
      showSuccess('Discount code archived.');
      setArchiveTarget(null);
      await loadData();
    }
  };

  const settleProvider = async (providerId: number, commissionIds: number[]) => {
    setSettlingProviderId(providerId);
    const result = await settleAdminCommissions(providerId, commissionIds);
    setSettlingProviderId(null);
    if (result.ok) {
      showSuccess(`${formatUsd(result.data.total_usd)} marked paid.`);
      await loadData();
    }
  };

  const normalizedSearch = search.trim().toLowerCase();
  const visibleCodes = codes.filter((code) => (
    (showArchived || !code.is_deleted)
    && (!normalizedSearch || `${code.code} ${code.provider_name} ${code.provider_email}`.toLowerCase().includes(normalizedSearch))
  ));

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: (theme) => theme.palette.mode === 'light' ? '#f4f6f7' : theme.palette.background.default }}>
      <Banner />
      <Container maxWidth="xl" sx={{ pt: 10, pb: 8 }}>
        <Stack spacing={2.5}>
          <Typography variant="h4" component="h1" fontWeight="bold">Discounts and commissions</Typography>
          <Paper sx={{ px: 2 }}>
            <Tabs value={tab} onChange={(_event, value) => setTab(value)}>
              <Tab label="Discount codes" />
              <Tab label="Commissions" />
              <Tab label="Payment history" />
            </Tabs>
          </Paper>

          {loading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box> : null}

          {!loading && tab === 0 ? (
            <Paper sx={{ p: 2.5 }}>
              <Stack spacing={2}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between">
                  <TextField size="small" label="Search codes or providers" value={search} onChange={(event) => setSearch(event.target.value)} sx={{ minWidth: 300 }} />
                  <Stack direction="row" spacing={1} alignItems="center">
                    <FormControlLabel control={<Checkbox checked={showArchived} onChange={(event) => setShowArchived(event.target.checked)} />} label="Show archived" />
                    <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>New code</Button>
                  </Stack>
                </Stack>
                <TableContainer>
                  <Table size="small">
                    <TableHead><TableRow>
                      <TableCell>Code</TableCell><TableCell>Provider</TableCell><TableCell align="right">Trial</TableCell>
                      <TableCell align="right">Commission</TableCell><TableCell>Ends</TableCell><TableCell align="right">New users</TableCell>
                      <TableCell>Status</TableCell><TableCell align="right">Actions</TableCell>
                    </TableRow></TableHead>
                    <TableBody>
                      {visibleCodes.map((code) => (
                        <TableRow key={code.discount_code_id} hover>
                          <TableCell sx={{ fontWeight: 700 }}>{code.code}</TableCell>
                          <TableCell>{code.provider_name}<Typography variant="caption" display="block" color="text.secondary">{code.provider_email}</Typography></TableCell>
                          <TableCell align="right">{code.trial_days} days</TableCell><TableCell align="right">{formatUsd(code.commission_usd)}</TableCell>
                          <TableCell>{code.ends_on || 'No end date'}</TableCell><TableCell align="right">{code.new_users}</TableCell>
                          <TableCell>{code.is_deleted ? 'Archived' : code.is_enabled ? 'Enabled' : 'Disabled'}</TableCell>
                          <TableCell align="right">
                            {!code.is_deleted ? <>
                              <Tooltip title="Edit"><IconButton onClick={() => openEdit(code)}><EditOutlinedIcon /></IconButton></Tooltip>
                              <Tooltip title="Archive"><IconButton onClick={() => setArchiveTarget(code)}><ArchiveOutlinedIcon /></IconButton></Tooltip>
                            </> : null}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Stack>
            </Paper>
          ) : null}

          {!loading && tab === 1 ? (
            <Paper sx={{ p: 2.5 }}>
              <TableContainer><Table size="small">
                <TableHead><TableRow><TableCell>Content provider</TableCell><TableCell align="right">Unpaid users</TableCell><TableCell align="right">Amount owed</TableCell><TableCell align="right">Action</TableCell></TableRow></TableHead>
                <TableBody>{(commissions?.providers || []).map((provider) => (
                  <TableRow key={provider.content_provider_id} hover>
                    <TableCell>{provider.name}<Typography variant="caption" display="block" color="text.secondary">{provider.email}</Typography></TableCell>
                    <TableCell align="right">{provider.unpaid_count}</TableCell><TableCell align="right" sx={{ fontWeight: 700 }}>{formatUsd(provider.unpaid_total_usd)}</TableCell>
                    <TableCell align="right"><Button variant="outlined" disabled={!provider.unpaid_count || settlingProviderId === provider.content_provider_id} onClick={() => void settleProvider(provider.content_provider_id, provider.unpaid_commission_ids)}>{settlingProviderId === provider.content_provider_id ? 'Updating...' : 'Mark current'}</Button></TableCell>
                  </TableRow>
                ))}</TableBody>
              </Table></TableContainer>
            </Paper>
          ) : null}

          {!loading && tab === 2 ? (
            <Stack spacing={1.5}>
              {(commissions?.payment_batches || []).length === 0 ? <Alert severity="info">No commission payments have been recorded.</Alert> : null}
              {(commissions?.payment_batches || []).map((batch) => (
                <Accordion key={batch.commission_payment_batch_id} disableGutters>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Stack direction={{ xs: 'column', md: 'row' }} width="100%" justifyContent="space-between" pr={2}>
                      <Typography fontWeight={700}>{batch.provider_name} · {formatUsd(batch.total_usd)}</Typography>
                      <Typography color="text.secondary">{new Date(batch.paid_at).toLocaleString()} · {batch.paid_by}</Typography>
                    </Stack>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{batch.provider_email} · {batch.commission_count} commissions</Typography>
                    <Table size="small"><TableHead><TableRow><TableCell>Code</TableCell><TableCell>Account</TableCell><TableCell>Earned</TableCell><TableCell align="right">Amount</TableCell></TableRow></TableHead>
                      <TableBody>{batch.commissions.map((commission) => <TableRow key={commission.discount_commission_id}><TableCell>{commission.code}</TableCell><TableCell>{commission.account_id}</TableCell><TableCell>{new Date(commission.earned_at).toLocaleString()}</TableCell><TableCell align="right">{formatUsd(commission.amount_usd)}</TableCell></TableRow>)}</TableBody>
                    </Table>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Stack>
          ) : null}
        </Stack>
      </Container>

      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? 'Edit discount code' : 'New discount code'}</DialogTitle>
        <DialogContent><Stack spacing={2} sx={{ pt: 1 }}>
          <TextField label="Code" value={draft.code} disabled={Boolean(editing) || saving} onChange={(event) => setDraft({ ...draft, code: event.target.value })} />
          <TextField label="Content provider name" value={draft.provider_name} disabled={saving} onChange={(event) => setDraft({ ...draft, provider_name: event.target.value })} />
          <TextField label="Content provider email" type="email" value={draft.provider_email} disabled={saving} onChange={(event) => setDraft({ ...draft, provider_email: event.target.value })} />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField fullWidth label="Trial days" type="number" value={draft.trial_days} disabled={saving} onChange={(event) => setDraft({ ...draft, trial_days: event.target.value })} />
            <TextField fullWidth label="Commission (USD)" type="number" value={draft.commission_usd} disabled={saving} onChange={(event) => setDraft({ ...draft, commission_usd: event.target.value })} />
          </Stack>
          <TextField label="Ending date (Eastern Time)" type="date" value={draft.ends_on} disabled={saving} onChange={(event) => setDraft({ ...draft, ends_on: event.target.value })} InputLabelProps={{ shrink: true }} />
          <FormControlLabel control={<Switch checked={draft.is_enabled} disabled={saving} onChange={(event) => setDraft({ ...draft, is_enabled: event.target.checked })} />} label="Code enabled" />
        </Stack></DialogContent>
        <DialogActions><Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button><Button variant="contained" onClick={() => void saveCode()} disabled={saving}>{saving ? 'Saving...' : 'Save'}</Button></DialogActions>
      </Dialog>
      <ConfirmDialog open={Boolean(archiveTarget)} title="Archive discount code?" message="The code can no longer be used, but its redemption and commission history will remain available." onConfirm={() => void archiveCode()} onCancel={() => setArchiveTarget(null)} />
    </Box>
  );
}